import { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledInputSelect } from '@components/controlled-input-select';
import { REQUIRED_TEXT_ERROR } from '@components/dynamic-input-fields/src/utils';
import { IconImportCSV } from '@components/icons';
import { InputCsvParser } from '@components/input-csv-parser';
import { InputCsvParserProps } from '@components/input-csv-parser/src/input-csv-parser.type';
import { InputSelectAdmission } from '@components/input-select-admission';
import { LabelPrimary } from '@components/label';
import { Loader } from '@components/loader';
import { ModalStickyFooter } from '@components/modals/modal-sticky-footer';
import {
  ImportCSVForm,
  SPLITTER_COMMA,
  SPLITTER_SEMICOLON,
} from '@components/process-import-csv/src/process-import-csv.type';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { Admission, ADMISSION_STATUS_HIDDEN_BY_TRIGGER } from '@domain/admission';
import { ActiveChannel } from '@domain/channel';
import { AdmissionResponse, useGetAdmissions } from '@hooks/admissions/use-get-admissions';
import { useGetChannels } from '@hooks/events/use-get-event-channels';
import { useGetActiveChannels } from '@hooks/use-active-channels';

import { shouldFilterOfferIfIsSiae } from '../utils/filter-offer-if-siae';

export interface ImportCSVSelectType extends Omit<InputCsvParserProps, 'delimiter'> {
  onContinue: () => void;
  offer?: Admission;
}

export const SelectType = ({ onContinue, offer, fileName, rows, setRows, setFileName }: ImportCSVSelectType) => {
  const { t } = useTranslation();
  const {
    companyClubState: { isSiaeImportLimited },
  } = useContext(CompanyClubContext);
  const {
    formState: { isValid },
    watch,
  } = useFormContext<ImportCSVForm>();

  const [channelList, setChannelList] = useState<ActiveChannel[]>([]);
  const SPLITTER_LIST = [
    { id: SPLITTER_COMMA, name: t('common:splitterComma') },
    { id: SPLITTER_SEMICOLON, name: t('common:splitterSemicolon') },
  ];
  const eventId = watch('eventId');
  const offerId = watch('offerId');
  const splitter = watch('splitter');
  const { data: admissionList, isInitialLoading: isAdmissionListLoading } = useGetAdmissions({
    eventId,
    options: { enabled: !!eventId },
  });
  const { data: activeChannels } = useGetActiveChannels({
    offerId,
    enabled: !!offerId,
  });
  const { data: eventChannels } = useGetChannels({ eventId, enabled: !!eventId && !!offerId });

  useEffect(() => {
    if (eventChannels && activeChannels && offer) {
      const channelsFormatted = eventChannels
        .filter((channel) => channel.isEnabled)
        .map((channel) => ({
          ...channel.channel,
          maxQuantity: null,
        }));

      if (activeChannels.isForAllChannels) {
        setChannelList(channelsFormatted);
        return;
      }

      setChannelList(activeChannels.channels);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannels, eventChannels, offer]);

  const filterCondition = (selectedOffer: Admission) =>
    !selectedOffer.hasMandatoryAddOns &&
    selectedOffer.status !== ADMISSION_STATUS_HIDDEN_BY_TRIGGER &&
    !selectedOffer.settings.isHidden &&
    shouldFilterOfferIfIsSiae(selectedOffer, isSiaeImportLimited);

  // We have to filter all offers with mandatory addons. Otherwise, the box office won't work
  const filteredAdmissionList: AdmissionResponse = {
    bottleService: admissionList?.bottleService.filter((bottle) => filterCondition(bottle)) ?? [],
    guestList: admissionList?.guestList.filter((guest) => filterCondition(guest)) ?? [],
    ticket: admissionList?.ticket.filter((ticket) => filterCondition(ticket)) ?? [],
  };
  return (
    <>
      <header className='flex flex-row items-center px-4 pt-8 mb-8 sm:px-20 sm:pt-12'>
        <span className='flex flex-grow items-center gap-3 text-headline-l text-left text-primary'>
          <IconImportCSV className='w-8 h-8' />
          <LabelPrimary>{t('common:importCsvTitle')}</LabelPrimary>
        </span>
      </header>
      <div className='flex flex-col gap-6 px-4 pb-6 sm:px-20 sm:pb-12'>
        {isAdmissionListLoading && <Loader />}
        {!!admissionList && (
          <InputSelectAdmission admissionList={filteredAdmissionList} eventId={eventId} selectedOffer={offer} />
        )}
        {offer && (
          <>
            <ControlledInputSelect
              fieldRegisterName='channelId'
              rules={{ required: { message: t(REQUIRED_TEXT_ERROR), value: true } }}
              options={channelList}
              label={t('invitations:processModalChannelSelectorTitle')}
            />
            <ControlledInputSelect
              fieldRegisterName='splitter'
              rules={{ required: { message: t(REQUIRED_TEXT_ERROR), value: true } }}
              options={SPLITTER_LIST}
              label={t('common:selectSplitter')}
            />
            <InputCsvParser
              delimiter={splitter}
              fileName={fileName}
              rows={rows}
              setRows={setRows}
              setFileName={setFileName}
            />
          </>
        )}
      </div>
      <ModalStickyFooter
        isSubmitDisabled={!isValid || rows?.length === 0 || !fileName}
        primaryButtonText={t('offers:continueButton')}
        onPrimaryButtonClick={onContinue}
      />
    </>
  );
};
